// src/views/OverlapView.js
import React, { useState, useMemo } from 'react';
import { Search, Loader2, X, Layers, AlertCircle, DollarSign, PieChart as PieIcon, BarChart3, Download, Plus, ShieldAlert } from 'lucide-react';
import { API_BASE } from '../api/db';

export default function OverlapView({ themeClasses, portfolio }) {
  const [tickers, setTickers] = useState([]);
  const [inputVal, setInputVal] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [allocations, setAllocations] = useState({});

  const importPortfolio = () => {
     const etfs = portfolio.filter(p => p.type === 'ETF' || p.type === 'Stock').map(p => p.ticker); // Some systems misclassify ETFs as Stocks
     const unique = [...new Set(etfs)];
     if (unique.length < 2) {
         setError("You need at least 2 assets in your portfolio to compare overlap.");
         return;
     }
     setTickers(unique);
     const initialAlloc = {};
     portfolio.forEach(p => {
         initialAlloc[p.ticker] = (initialAlloc[p.ticker] || 0) + (p.shares * p.costBasis);
     });
     setAllocations(initialAlloc);
     setError('');
  };

  const addTicker = (e) => {
     e.preventDefault();
     const t = inputVal.toUpperCase().trim();
     if (t && !tickers.includes(t)) {
         setTickers([...tickers, t]);
         setAllocations(prev => ({...prev, [t]: 10000}));
     }
     setInputVal('');
  };

  const removeTicker = (t) => {
     setTickers(tickers.filter(x => x !== t));
     const newAlloc = {...allocations};
     delete newAlloc[t];
     setAllocations(newAlloc);
  };

  const runAnalysis = async () => {
     if (tickers.length < 2) {
         setError("Please add at least 2 Funds/ETFs.");
         return;
     }
     setLoading(true);
     setError('');
     try {
         const res = await fetch(`${window.location.origin}${API_BASE}/model/overlap`, {
             method: 'POST',
             headers: { 'Content-Type': 'application/json' },
             body: JSON.stringify({ tickers })
         });
         const json = await res.json();
         if (!res.ok) throw new Error(json.error || 'Failed to analyze overlap.');
         setData(json);
     } catch (err) {
         setError(err.message);
     } finally {
         setLoading(false);
     }
  };

  const exposureList = useMemo(() => {
    if (!data || !data.overlap_assets) return [];
    let totalInvested = Object.values(allocations).reduce((a,b) => a+b, 0) || 1;

    return data.overlap_assets.map(asset => {
        let totalValueInAsset = 0;
        const fundBreakdown = {};

        tickers.forEach(t => {
            const w = asset.weights[t] || 0;
            // Normalize: Some APIs return 0.05 for 5%, others return 5.0. 
            const normalizedWeight = w > 1 ? w / 100 : w; 
            const fundAllocation = allocations[t] || 0;
            const value = fundAllocation * normalizedWeight;
            totalValueInAsset += value;
            fundBreakdown[t] = value;
        });

        const effectiveWeight = (totalValueInAsset / totalInvested) * 100;
        return { symbol: asset.symbol, totalValue: totalValueInAsset, effectiveWeight, fundBreakdown };
    }).sort((a,b) => b.totalValue - a.totalValue);
  }, [data, allocations, tickers]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-full max-w-[1600px] mx-auto overflow-x-hidden">
      
      {/* Sidebar: Inputs & Allocations */}
      <div className={`col-span-1 lg:col-span-4 flex flex-col gap-6`}>
        <div className={`${themeClasses.bgCard} border ${themeClasses.border} rounded-3xl p-6 shadow-xl`}>
          <h3 className="text-[10px] font-black text-indigo-500 uppercase mb-4 tracking-widest flex items-center gap-2">
            <Layers size={14}/> Fund Overlap Tool
          </h3>
          <p className={`text-xs ${themeClasses.textMuted} mb-6 leading-relaxed font-medium`}>
            Identify secret concentrations in your portfolio by extracting the underlying holdings of your ETFs.
          </p>

          <button onClick={importPortfolio} className={`w-full py-3 mb-6 rounded-xl border border-emerald-500/30 text-emerald-500 bg-emerald-500/10 hover:bg-emerald-500 hover:text-white transition-colors text-[10px] font-black uppercase tracking-widest flex justify-center items-center gap-2`}>
            <Download size={14}/> Import from Portfolio
          </button>

          <form onSubmit={addTicker} className="relative mb-6">
             <input value={inputVal} onChange={e => setInputVal(e.target.value)} placeholder="ADD ETF TICKER..." className={`w-full ${themeClasses.input} border ${themeClasses.border} rounded-xl py-3 px-4 text-xs font-bold outline-none focus:border-indigo-500 ${themeClasses.textHead}`} />
             <button type="submit" className="absolute right-4 top-3 text-indigo-500 hover:text-indigo-400 transition-colors"><Plus size={18} /></button>
          </form>

          <div className="flex flex-wrap gap-2 mb-6">
            {tickers.map(t => (
               <div key={t} className={`flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 px-3 py-1.5 rounded-lg text-xs font-black`}>
                 {t} <button onClick={() => removeTicker(t)} className="hover:text-red-400 transition-colors"><X size={12}/></button>
               </div>
            ))}
            {tickers.length === 0 && <p className={`text-[10px] uppercase font-bold tracking-widest ${themeClasses.textMuted} opacity-50`}>No funds selected</p>}
          </div>

          <button onClick={runAnalysis} disabled={loading || tickers.length < 2} className={`w-full py-4 rounded-xl text-white font-black uppercase tracking-widest text-xs transition-colors shadow-lg shadow-indigo-500/20 disabled:opacity-50 ${loading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-500'}`}>
            {loading ? <Loader2 className="animate-spin mx-auto" size={16}/> : 'Scan for Overlap'}
          </button>
          
          {error && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3">
              <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5"/>
              <p className="text-red-400 text-xs font-medium leading-relaxed">{error}</p>
            </div>
          )}
        </div>

        {data && (
          <div className={`${themeClasses.bgCard} border ${themeClasses.border} rounded-3xl p-6 shadow-xl flex-1`}>
            <h3 className="text-[10px] font-black text-emerald-500 uppercase mb-4 tracking-widest flex items-center gap-2">
              <DollarSign size={14}/> Capital Allocation
            </h3>
            <p className={`text-xs ${themeClasses.textMuted} mb-6 leading-relaxed font-medium`}>
              Adjust your theoretical dollar amounts below to calculate your true financial exposure.
            </p>
            <div className="space-y-4">
              {tickers.map(t => (
                <div key={t}>
                  <label className={`text-[10px] font-black ${themeClasses.textMuted} uppercase tracking-widest flex justify-between mb-2`}>
                    <span>{t} Investment</span>
                    <span className="text-emerald-500">${(allocations[t] || 0).toLocaleString()}</span>
                  </label>
                  <input type="range" min="0" max="100000" step="1000" value={allocations[t] || 0} onChange={(e) => setAllocations(prev => ({...prev, [t]: Number(e.target.value)}))} className="w-full accent-indigo-500" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Main Panel: Results */}
      <div className={`col-span-1 lg:col-span-8 flex flex-col ${themeClasses.bgCard} border ${themeClasses.border} rounded-3xl p-6 shadow-xl h-[80vh] lg:h-auto`}>
         <h3 className="text-[10px] font-black text-indigo-500 uppercase mb-4 tracking-widest flex items-center gap-2 shrink-0">
           <BarChart3 size={14}/> True Underlying Exposure
         </h3>

         {!data ? (
           <div className={`flex-1 flex flex-col items-center justify-center ${themeClasses.textMuted} opacity-50`}>
              <PieIcon size={48} className="mb-4" />
              <p className="text-xs uppercase tracking-widest font-black">Awaiting Analysis</p>
           </div>
         ) : exposureList.length === 0 ? (
           <div className={`flex-1 flex flex-col items-center justify-center text-emerald-500 opacity-80`}>
              <ShieldAlert size={48} className="mb-4" />
              <p className="text-xs uppercase tracking-widest font-black">No Overlap Detected!</p>
              <p className={`text-xs font-medium mt-2 ${themeClasses.textMuted}`}>These funds hold completely different assets.</p>
           </div>
         ) : (
           <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-3">
              {exposureList.slice(0, 50).map((asset, idx) => (
                <div key={idx} className={`${themeClasses.input} border ${themeClasses.border} rounded-xl p-4 transition-all hover:border-indigo-500/50 group`}>
                   <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className={`text-lg font-black ${themeClasses.textHead} group-hover:text-indigo-400 transition-colors`}>{asset.symbol}</h4>
                        <p className={`text-[10px] font-black uppercase tracking-widest ${themeClasses.textMuted} mt-1`}>
                          Present in {Object.keys(asset.fundBreakdown).filter(k => asset.fundBreakdown[k] > 0).length} Funds
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-mono font-black text-emerald-500">${asset.totalValue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                        <p className={`text-[10px] font-black uppercase tracking-widest ${themeClasses.textMuted} mt-1`}>
                          {asset.effectiveWeight.toFixed(2)}% of Portfolio
                        </p>
                      </div>
                   </div>

                   <div className="flex w-full h-2 rounded-full overflow-hidden bg-black/20">
                      {tickers.map((t, i) => {
                         const val = asset.fundBreakdown[t] || 0;
                         if (val === 0) return null;
                         const pct = (val / asset.totalValue) * 100;
                         const bgColors = ['bg-indigo-500', 'bg-emerald-500', 'bg-amber-500', 'bg-pink-500', 'bg-blue-500'];
                         return (
                           <div key={t} style={{ width: `${pct}%` }} className={`${bgColors[i % bgColors.length]} h-full`} title={`${t}: $${val.toFixed(2)}`} />
                         );
                      })}
                   </div>
                </div>
              ))}
           </div>
         )}
      </div>

    </div>
  );
}